import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { StoreProvider } from "./lib/stores";
import StoreThemeSync from "./components/StoreThemeSync";
import Layout from "./components/Layout";
import PublicLayout from "./components/PublicLayout";
import Landing from "./pages/public/Landing";
import About from "./pages/public/About";
import Features from "./pages/public/Features";
import Pricing from "./pages/public/Pricing";
import Contact from "./pages/public/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import ProductVariantsForm from "./pages/ProductVariantsForm";
import POS from "./pages/POS";
import Customers from "./pages/Customers";
import Sales from "./pages/Sales";
import SaleInvoice from "./pages/SaleInvoice";
import SaleEdit from "./pages/SaleEdit";
import Expenses from "./pages/Expenses";
import Staff from "./pages/Staff";
import Attendance from "./pages/Attendance";
import Subscription from "./pages/Subscription";
import Settings from "./pages/Settings";
import ChangePassword from "./pages/ChangePassword";
import Stores from "./pages/Stores";
import StoreProfile from "./pages/StoreProfile";
import Collections from "./pages/Collections";
import Purchases from "./pages/Purchases";
import PurchaseForm from "./pages/PurchaseForm";
import PurchaseDetail from "./pages/PurchaseDetail";
import Categories from "./pages/Categories";
import Reports from "./pages/Reports";
import PublicInvoice from "./pages/PublicInvoice";
import SuperAdmin from "./pages/SuperAdmin";

function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <StoreProvider>
      <BrowserRouter>
        <StoreThemeSync />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/invoice/:token" element={<PublicInvoice />} />
          <Route path="/superadmin" element={<AdminGate><SuperAdmin /></AdminGate>} />
          <Route path="/app" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="pos" element={<POS />} />
            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<ProductVariantsForm />} />
            <Route path="products/variants/new" element={<Navigate to="/app/products/new" replace />} />
            <Route path="products/:id/edit" element={<ProductVariantsForm />} />
            <Route path="customers" element={<Customers />} />
            <Route path="sales" element={<Sales />} />
            <Route path="sales/:id/edit" element={<SaleEdit />} />
            <Route path="sales/:id" element={<SaleInvoice />} />
            <Route path="collections" element={<Collections />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="purchases/new" element={<PurchaseForm />} />
            <Route path="purchases/:id" element={<PurchaseDetail />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="staff" element={<Staff />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/change-password" element={<ChangePassword />} />
            <Route path="stores" element={<Stores />} />
            <Route path="stores/:id/profile" element={<StoreProfile />} />
            <Route path="categories" element={<Categories />} />
            <Route path="reports" element={<Reports />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </StoreProvider>
    </AuthProvider>
  );
}

export default App;
